import * as Tooltip from "@radix-ui/react-tooltip";
import { cn } from "../../lib/utils";
import useSignedUrlToGetFile from "../../hooks/useSignedUrlToGetFile";
import {
  PublicUserOrganization,
  UserOrganization,
} from "../../__generated__/graphql";
import { useUser } from "../../contexts/UserContext/hooks/useUser";
import { FaCamera } from "react-icons/fa6";

export default function Avatar({
  userOrganization,
  shape = "circle",
  size = "medium",
  className,
}: {
  userOrganization: PublicUserOrganization | UserOrganization | null;
  shape?: "circle" | "square";
  size?: "small" | "medium" | "large" | "xlarge" | "xs";
  className?: string;
}) {
  const { userOrganization: currentUserOrganization } = useUser();
  const { data: signedUrlData, loading: isLoadingSignedUrl } =
    useSignedUrlToGetFile(userOrganization?.avatarBucketKey);

  const hasAvatar = !isLoadingSignedUrl && signedUrlData?.signedUrlToGetFile;
  const isCurrentUser = userOrganization?.id === currentUserOrganization?.id;

  const handleInitialsOrPicture = () => {
    if (isLoadingSignedUrl) {
      return <div className="w-full h-full bg-accent" />;
    }

    if (signedUrlData?.signedUrlToGetFile) {
      return (
        <img
          src={signedUrlData.signedUrlToGetFile}
          className={cn(
            "w-full h-full object-cover border border-border rounded-full",
            shape === "square" && "rounded-md"
          )}
        />
      );
    }

    if (isCurrentUser) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-card">
          <FaCamera
            className={cn(
              "w-4 h-4 text-secondary",
              size === "xs" && "w-2 h-2",
              size === "small" && "w-3 h-3",
              size === "medium" && "w-4 h-4",
              size === "large" && "w-5 h-5",
              size === "xlarge" && "w-6 h-6"
            )}
          />
        </div>
      );
    }

    if (!userOrganization?.name) return "";

    const formattedName = userOrganization.name.toUpperCase();
    const space = " ";

    if (formattedName.includes(space)) {
      const [firstName, lastName] = formattedName.split(space);
      return `${firstName[0]}${lastName[0]}`;
    }

    if (formattedName.length > 0) {
      return `${formattedName[0]}${formattedName[1]}`;
    }

    return "";
  };

  if (!userOrganization) return null;

  return (
    <Tooltip.Provider>
      <Tooltip.Root delayDuration={0}>
        <Tooltip.Trigger asChild>
          <div
            className={cn(
              className,
              "rounded-full overflow-hidden flex items-center justify-center text-dark text-sm font-extrabold select-none",
              shape === "square" && "rounded-md",
              size === "xs" && "w-5 h-5 text-[8px]",
              size === "small" && "w-6 h-6 text-xs",
              size === "medium" && "w-8 h-8",
              size === "large" && "w-12 h-12",
              size === "xlarge" && "w-16 h-16",
              !hasAvatar && "border-2 border-[#aa8b00]",
              !hasAvatar && "bg-accent",
              !hasAvatar && isCurrentUser && "border border-secondary"
            )}
          >
            {handleInitialsOrPicture()}
          </div>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className="rounded-md bg-black px-4 py-2 text-sm text-white"
            sideOffset={5}
          >
            {userOrganization.name}
            <Tooltip.Arrow className="fill-black" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
